import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { TABLE_CELL_ALIGN, TABLE_CELL_KIND, TABLE_DENSITY } from './types/table-variants-enums';
import { TableHead } from '@/components/ui/table';
import { useTableDensity } from '@/components/ui/table-context';
import { cn } from '@/lib/utils';

import { SORT_ARIA_VALUE, SORT_DIRECTION } from './types/sortable-table-header-variants-enums';



export interface SortableTableHeaderProps extends Omit<ComponentProps<typeof TableHead>, 'onClick'> {
  children: ReactNode;
  sortDirection?: SORT_DIRECTION;
  onSort?: () => void;
  sortLabel?: string;
}

function sortAriaValue(direction: SORT_DIRECTION): SORT_ARIA_VALUE {
  if (direction === SORT_DIRECTION.ASC) {
    return SORT_ARIA_VALUE.ASCENDING;
  }
  if (direction === SORT_DIRECTION.DESC) {
    return SORT_ARIA_VALUE.DESCENDING;
  }
  return SORT_ARIA_VALUE.NONE;
}

function SortIcon({ direction }: { direction: SORT_DIRECTION }) {
  if (direction === SORT_DIRECTION.ASC) {
    return <ArrowUp className="size-3.5 shrink-0" aria-hidden="true" />;
  }
  if (direction === SORT_DIRECTION.DESC) {
    return <ArrowDown className="size-3.5 shrink-0" aria-hidden="true" />;
  }
  return <ArrowUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden="true" />;
}

function getButtonAlignmentClassName(
  align: TABLE_CELL_ALIGN | undefined,
  kind: TABLE_CELL_KIND | undefined
): string | undefined {
  if (
    align === TABLE_CELL_ALIGN.END ||
    kind === TABLE_CELL_KIND.NUMERIC ||
    kind === TABLE_CELL_KIND.ACTION
  ) {
    return 'justify-end text-right';
  }
  if (align === TABLE_CELL_ALIGN.CENTER) {
    return 'justify-center text-center';
  }
  return undefined;
}

export function SortableTableHeader({
  children,
  sortDirection = SORT_DIRECTION.NONE,
  onSort,
  sortLabel,
  className,
  align,
  kind,
  ...props
}: SortableTableHeaderProps) {
  const density = useTableDensity();
  const label =
    sortLabel ?? (typeof children === 'string' || typeof children === 'number' ? String(children) : 'Sort');

  return (
    <TableHead
      aria-sort={sortAriaValue(sortDirection)}
      className={cn('p-0', className)}
      align={align}
      kind={kind}
      {...props}
    >
      <button
        type="button"
        className={cn(
          density === undefined &&
            'inline-flex h-12 w-full items-center gap-1.5 px-4 text-left text-sm font-medium text-foreground',
          density === TABLE_DENSITY.COMPACT &&
            'inline-flex h-9 w-full items-center gap-1.5 px-3 text-left type-table-header font-medium text-content-secondary',
          density === TABLE_DENSITY.DEFAULT &&
            'inline-flex h-10 w-full items-center gap-1.5 px-4 text-left type-table-header font-medium text-content-secondary',
          density === undefined
            ? 'hover:bg-interaction-hover hover:text-interaction-hover-foreground'
            : 'pointer-fine:hover:bg-neutral-interaction-hover pointer-fine:hover:text-content-primary',
          density === undefined
            ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
            : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
          'disabled:pointer-events-none disabled:opacity-50',
          getButtonAlignmentClassName(align, kind)
        )}
        onClick={onSort}
        disabled={!onSort}
        aria-label={`Sort by ${label}`}
      >
        <span className="min-w-0 truncate">{children}</span>
        <SortIcon direction={sortDirection} />
      </button>
    </TableHead>
  );
}
